/* eslint-disable no-multi-assign */
/* eslint-disable no-param-reassign */
import * as React from 'react';

import { addMonths, addYears, isAfter, isBefore, isSameDay, isSameMonth, isWithinInterval, max, min } from 'date-fns';

import { defaultRanges } from '../defaults';
import { getValidatedMonths, parseOptionalDate } from '../utils';
import Menu from './Menu';

export const MARKERS = {
  FIRST_MONTH: Symbol('firstMonth'),
  SECOND_MONTH: Symbol('secondMonth'),
};

const DateRangePicker = ({
  open,
  onChange = () => {},
  initialDateRange,
  minDate,
  maxDate,
  definedRanges = defaultRanges,
  placeholderText = 'Select Date Range',
  onClose = () => {},
  onClear = () => {},
  onSelect = () => {},
  showPredefinedRanges = false,
  enableEmptyDateRangeToBeSelected = false,
  displayTwoMonthsLayout = true,
  displayCancelButton = true,
  displayClearButton = true,
}) => {
  const today = new Date();

  const minDateValid = parseOptionalDate(minDate, addYears(today, -10));
  const maxDateValid = parseOptionalDate(maxDate, addYears(today, 10));
  const [initialFirstMonth, initialSecondMonth] = getValidatedMonths(
    initialDateRange || {},
    minDateValid,
    maxDateValid
  );

  const [dateRange, setDateRange] = React.useState({ ...initialDateRange });
  const [hoverDay, setHoverDay] = React.useState();
  const [firstMonth, setFirstMonth] = React.useState(initialFirstMonth || today);
  const [secondMonth, setSecondMonth] = React.useState(initialSecondMonth || addMonths(firstMonth, 1));

  const { startDate, endDate } = dateRange;

  // handlers
  const setFirstMonthValidated = (date) => {
    if (!displayTwoMonthsLayout || isBefore(date, secondMonth)) {
      setFirstMonth(date);
    }
  };

  const setSecondMonthValidated = (date) => {
    if (isAfter(date, firstMonth)) {
      setSecondMonth(date);
    }
  };

  const setDateRangeValidated = (range) => {
    // let { startDate: newStart, endDate: newEnd } = range;
    const modifiedRange = { ...range };
    let { startDate: newStart, endDate: newEnd } = modifiedRange;

    if (newStart && newEnd) {
      modifiedRange.startDate = newStart = max([newStart, minDateValid]);
      modifiedRange.endDate = newEnd = min([newEnd, maxDateValid]);

      setDateRange(range);
      onChange(range);

      setFirstMonth(newStart);
      setSecondMonth(isSameMonth(newStart, newEnd) ? addMonths(newStart, 1) : newEnd);
    } else {
      const emptyRange = {};

      setDateRange(emptyRange);
      onChange(emptyRange);

      setFirstMonth(today);
      setSecondMonth(addMonths(firstMonth, 1));
    }
  };

  const onDayClick = (day) => {
    if (startDate && !endDate && !isBefore(day, startDate)) {
      const newRange = { startDate, endDate: day };
      onChange(newRange);
      setDateRange(newRange);
    } else {
      setDateRange({ startDate: day, endDate: undefined });
    }
    setHoverDay(day);
  };

  const onMonthNavigate = (marker, action) => {
    if (marker === MARKERS.FIRST_MONTH) {
      const firstNew = addMonths(firstMonth, action);
      const secondNew = addMonths(secondMonth, action);
      setSecondMonth(secondNew);
      if (!displayTwoMonthsLayout || isBefore(firstNew, secondMonth)) setFirstMonth(firstNew);
    } else {
      const firstNew = addMonths(firstMonth, action);
      const secondNew = addMonths(secondMonth, action);
      setFirstMonth(firstNew);
      if (isBefore(firstMonth, secondNew)) setSecondMonth(secondNew);
    }
  };

  const onDayHover = (date) => {
    if (startDate && !endDate) {
      if (!hoverDay || !isSameDay(date, hoverDay)) {
        setHoverDay(date);
      }
    }
  };

  const onClearHandler = () => {
    setDateRange({});
    if (onClear) {
      onClear();
    }
  };

  // helpers
  const inHoverRange = (day) =>
    startDate &&
    !endDate &&
    hoverDay &&
    isAfter(hoverDay, startDate) &&
    isWithinInterval(day, { start: startDate, end: hoverDay });

  const helpers = {
    inHoverRange,
  };

  const handlers = {
    onDayClick,
    onDayHover,
    onMonthNavigate,
  };

  return open ? (
    <Menu
      dateRange={dateRange}
      minDate={minDateValid}
      maxDate={maxDateValid}
      ranges={definedRanges}
      firstMonth={firstMonth}
      secondMonth={secondMonth}
      setFirstMonth={setFirstMonthValidated}
      setSecondMonth={setSecondMonthValidated}
      setDateRange={setDateRangeValidated}
      helpers={helpers}
      handlers={handlers}
      placeholderText={placeholderText}
      onClose={onClose}
      onClear={onClearHandler}
      onSelect={onSelect}
      showPredefinedRanges={showPredefinedRanges}
      enableEmptyDateRangeToBeSelected={enableEmptyDateRangeToBeSelected}
      displayTwoMonthsLayout={displayTwoMonthsLayout}
      displayCancelButton={displayCancelButton}
      displayClearButton={displayClearButton}
    />
  ) : null;
};

export { DateRangePicker };
